import { Component, OnDestroy, OnInit, Signal, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {
  IAccountForm,
  IAccountFormPayload,
  IEditAccountDetails,
  IMedicalSpecialty,
} from '@app/accounts/models/accounts.model';
import {IRole} from '@app/accounts/models/role.model';

import {toSignal} from '@angular/core/rxjs-interop';
import {
  loadEditCustomerAccountDetails,
  loadRoles,
  resetUpdateAccountStatus,
  updateAccount,
  updateAccountStatus,
} from '@app/accounts/states/accounts.actions';
import {
  selectEditCustomerAccountDetails,
  selectRoles,
  selectUpdateAccountStatus,
} from '@app/accounts/states/accounts.selector';
import {ObjectUtils} from '@app/core/utils/object.utils';
import {MinChars_Regex} from '@app/shared/constants';
import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {ERole} from '@app/shared/enums/role.enum';
import {ValidationService} from '@app/shared/services/validation.service';
import {selectCustomerOrganizationId} from '@app/shared/state/header/header.selector';
import {loadMedicalSpecialties} from '@app/signUp/state/sign-up.actions';
import {selectMedicalSpecialties} from '@app/signUp/state/sign-up.selector';
import {Store} from '@ngrx/store';
import {Observable, Subject, filter, map, takeUntil, tap} from 'rxjs';
import { Bind } from 'primeng/bind';
import { FloatLabel } from 'primeng/floatlabel';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { Select } from 'primeng/select';
import { Checkbox } from 'primeng/checkbox';
import { Button } from 'primeng/button';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-edit-account',
    templateUrl: './edit-account.component.html',
    styleUrl: './edit-account.component.scss',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        Bind,
        FloatLabel,
        InputText,
        Message,
        Select,
        Checkbox,
        Button,
        AsyncPipe,
        TranslatePipe,
    ],
})
export class EditAccountComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly formBuilder = inject(FormBuilder);
  private readonly validationService = inject(ValidationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private readonly unsubscribe$ = new Subject<void>();
  medicalSpecialties$: Observable<IMedicalSpecialty[]>;
  medicalSpecialties: IMedicalSpecialty[] = [];
  editAccountForm: FormGroup<IAccountForm>;
  isQualifiedProfessionalVisible = true;
  roles: Signal<IRole[]>;
  saveInProgress = false;
  customerId = 0;
  organizationId = 0;

  constructor() {
    this.medicalSpecialties$ = this.store.select(selectMedicalSpecialties).pipe(
      takeUntil(this.unsubscribe$),
      map(medicalSpecialties => medicalSpecialties ?? [])
    );

    this.editAccountForm = this.formBuilder.group({
      firstName: [
        '',
        [
          Validators.required,
          this.validationService.charactersDiacriticsWithMaxLimt,
          Validators.pattern(MinChars_Regex),
        ],
      ],
      lastName: [
        '',
        [
          Validators.required,
          this.validationService.charactersDiacriticsWithMaxLimt,
          Validators.pattern(MinChars_Regex),
        ],
      ],
      email: ['', [Validators.required]],
      medicalSpecialty: new FormControl<number | null>(null, Validators.required),
      doctorId: ['', [this.validationService.specialCharacterWithDigits]],
      role: new FormControl<number | null>(null, Validators.required),
      isQualifiedProfessional: [true, [Validators.requiredTrue]],
    });

    this.roles = toSignal(this.store.select(selectRoles), {initialValue: []});

    this.store
      .select(selectCustomerOrganizationId)
      .pipe(
        tap(organizationId => {
          this.organizationId = organizationId ?? 0;
          if (this.organizationId != 0) {
            this.loadEditCustomerAccountDetails();
          }
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.store.dispatch(loadRoles());
    this.loadMedicalSpecialties();
    this.loadEditCustomerAccountDetailsSubscription();
    this.store.dispatch(resetUpdateAccountStatus());
    this.redirectToAccountsSubscription();
    this.checkQualifiedProfessional();
  }

  onEditAccountCancel(): void {
    this.router.navigate([`/accounts`]);
  }

  onEditAccountSubmit(): void {
    if (this.editAccountForm.valid) {
      this.saveInProgress = true;
      this.store.dispatch(updateAccountStatus({status: EActionStatus.Pending}));
      this.store.dispatch(updateAccount({payload: this.getAddAccountPayload()}));
    }
  }

  private checkQualifiedProfessional() {
    this.editAccountForm.get('role')?.valueChanges.subscribe(value => {
      const role = this.roles().find(role => role.name === ERole.Doctor);
      if (value === role?.id) {
        this.editAccountForm.get('isQualifiedProfessional')?.setValidators([Validators.requiredTrue]);
        this.isQualifiedProfessionalVisible = true;
      } else {
        this.editAccountForm.get('isQualifiedProfessional')?.clearValidators();
        this.isQualifiedProfessionalVisible = false;
      }
      this.editAccountForm.get('isQualifiedProfessional')?.updateValueAndValidity();
    });
  }

  private loadMedicalSpecialties(): void {
    this.store.dispatch(loadMedicalSpecialties());
  }

  private getAddAccountPayload(): IAccountFormPayload {
    const payload = {
      id: this.customerId,
    } as IAccountFormPayload;

    if (this.editAccountForm.value.firstName) {
      payload.firstName = this.editAccountForm.value.firstName;
    }

    if (this.editAccountForm.value.lastName) {
      payload.lastName = this.editAccountForm.value.lastName;
    }

    if (this.editAccountForm.controls.email.getRawValue()) {
      payload.email = this.editAccountForm.controls.email.getRawValue()?.toString();
    }

    if (this.editAccountForm.value.doctorId) {
      payload.doctorId = this.editAccountForm.value.doctorId;
    }

    if (this.editAccountForm.value.medicalSpecialty) {
      payload.medicalSpecialtyId = this.editAccountForm.value.medicalSpecialty;
    }

    if (this.editAccountForm.value.role) {
      payload.roleId = this.editAccountForm.value.role;
    }

    payload.organizationId = this.organizationId;

    return payload;
  }

  private redirectToAccountsSubscription(): void {
    this.store
      .select(selectUpdateAccountStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        map(updateAccountStatus => {
          this.saveInProgress = false;
          if (updateAccountStatus == EActionStatus.Success) {
            this.router.navigate([`/accounts`]);
          }
        })
      )
      .subscribe();
  }

  private loadEditCustomerAccountDetails(): void {
    const customerId = this.getCustomerId();
    if (customerId) {
      this.store.dispatch(
        loadEditCustomerAccountDetails({
          customerId: customerId.toString(),
          organizationId: this.organizationId,
        })
      );
    }
  }

  private getCustomerId(): number {
    const customerId = this.route.snapshot.queryParamMap?.get('id') ?? '0';
    return parseInt(customerId);
  }

  private loadEditCustomerAccountDetailsSubscription() {
    this.store
      .select(selectEditCustomerAccountDetails)
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(editAccountDetails => !ObjectUtils.isEmpty(editAccountDetails)),
        tap((editAccountDetails: IEditAccountDetails) => {
          this.setEditAccountFormValues(editAccountDetails);
          this.editAccountForm.controls.email.disable();
          this.editAccountForm.controls.isQualifiedProfessional.disable();
          this.customerId = editAccountDetails.id;
        })
      )
      .subscribe();
  }

  private setEditAccountFormValues(editAccountDetails: IEditAccountDetails | null): void {
    this.editAccountForm.patchValue({
      firstName: editAccountDetails?.firstName,
      lastName: editAccountDetails?.lastName,
      email: editAccountDetails?.email,
      medicalSpecialty: editAccountDetails?.medicalSpecialtyId,
      doctorId: editAccountDetails?.doctorId,
      isQualifiedProfessional: true,
      role: editAccountDetails?.roleId,
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
